'use client';

import {
  forwardRef,
  useId,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { hqPlaySeatClick } from '../audio/hq-sounds';

type Tone = 'gold' | 'crimson' | 'ember' | 'silver';

const TONES: Record<
  Tone,
  {
    base: string;
    border: string;
    halo: string;
    spark: string;
    text: string;
    edge: string;
  }
> = {
  gold: {
    base: 'from-[#1c150a] via-[#0c0a06] to-[#1c150a]',
    border: 'border-amber-400/45 hover:border-amber-300/80',
    halo: 'bg-amber-400/40',
    spark: 'bg-amber-200',
    text: 'text-amber-100',
    edge: 'from-transparent via-amber-300/70 to-transparent',
  },
  crimson: {
    base: 'from-[#1a0808] via-[#0b0404] to-[#1a0808]',
    border: 'border-red-500/45 hover:border-red-400/80',
    halo: 'bg-red-500/40',
    spark: 'bg-red-300',
    text: 'text-red-100',
    edge: 'from-transparent via-red-400/70 to-transparent',
  },
  ember: {
    base: 'from-[#180a04] via-[#0a0502] to-[#180a04]',
    border: 'border-orange-500/45 hover:border-orange-400/80',
    halo: 'bg-orange-500/40',
    spark: 'bg-orange-300',
    text: 'text-orange-100',
    edge: 'from-transparent via-orange-400/70 to-transparent',
  },
  silver: {
    base: 'from-[#0f1319] via-[#060709] to-[#0f1319]',
    border: 'border-slate-300/35 hover:border-slate-200/70',
    halo: 'bg-slate-200/25',
    spark: 'bg-slate-200',
    text: 'text-slate-100',
    edge: 'from-transparent via-slate-200/60 to-transparent',
  },
};

interface RpgButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  tone?: Tone;
  size?: 'sm' | 'md' | 'lg';
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  silent?: boolean;
  active?: boolean;
  /**
   * When `true`, render as a Radix `Slot` so callers can wrap an
   * `<a>` (or any element) and inherit the full RpgButton styling,
   * including the spark / halo / wisp layers. Mirrors shadcn
   * `Button`'s `asChild` prop so `<Button asChild><a /></Button>`
   * usages (e.g. the "Get $TTC" Flaunch link in drawers) port 1:1.
   *
   * Implementation note: when `asChild`, the user's element is hoisted
   * to be the root via `<Slottable>` and the decorative spans are
   * rendered as its siblings-become-children. When `asChild` is false
   * we stay on a plain `<button>` and can wrap the text in
   * `<span className="relative">` to keep it stacked above the
   * absolute decorative layers (the default look).
   */
  asChild?: boolean;
}

export const RpgButton = forwardRef<HTMLButtonElement, RpgButtonProps>(function RpgButton(
  {
    children,
    className,
    tone = 'gold',
    size = 'md',
    iconLeft,
    iconRight,
    silent = false,
    active = false,
    asChild = false,
    disabled,
    onClick,
    onMouseEnter,
    ...rest
  },
  ref,
) {
  const id = useId();
  const [sparkKey, setSparkKey] = useState(0);
  const t = TONES[tone];

  const padding =
    size === 'sm' ? 'px-2.5 py-1 text-[11px]' : size === 'lg' ? 'px-5 py-2.5 text-[13px]' : 'px-3.5 py-1.5 text-[12px]';

  const sparks = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: `${id}-spark-${i}`,
        fromX: -18 + i * 7 + (i % 2 === 0 ? 0 : 4),
        toX: -22 + i * 9 + (i % 2 === 0 ? 0 : 6),
        delay: i * 0.04,
      })),
    [id],
  );

  const Comp: React.ElementType = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      {...rest}
      // Only real <button> elements accept the boolean `disabled`
      // attribute; when wrapping an anchor via `asChild`, we surface
      // the state to assistive tech via aria-disabled and the visual
      // dim styling below (the onClick handler also bails early).
      {...(!asChild ? { disabled } : { 'aria-disabled': disabled || undefined })}
      onMouseEnter={(e) => {
        if (!disabled) setSparkKey((k) => k + 1);
        onMouseEnter?.(e);
      }}
      onClick={(e) => {
        if (disabled) {
          // Anchors ignore `disabled`; block the click explicitly so
          // asChild-wrapped <a>s don't navigate while disabled.
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (!silent) hqPlaySeatClick();
        onClick?.(e);
      }}
      className={cn(
        'group/rpg relative inline-flex items-center justify-center gap-1.5 rounded-[5px] font-mono uppercase tracking-[0.15em]',
        'bg-gradient-to-b border overflow-visible select-none',
        'transition-[transform,box-shadow,filter] duration-150 will-change-transform',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60',
        t.base,
        t.border,
        t.text,
        padding,
        active && 'ring-1 ring-amber-300/60',
        disabled
          ? 'opacity-55 cursor-not-allowed saturate-[0.55]'
          : 'active:translate-y-[1px] active:shadow-[inset_0_3px_6px_rgba(0,0,0,0.6)]',
        className,
      )}
    >
      {/* gilded top edge */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-1 right-1 -top-px h-px bg-gradient-to-r opacity-70 transition-opacity',
          !disabled && 'group-hover/rpg:opacity-100',
          t.edge,
        )}
      />
      {/* gilded bottom edge */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-2 right-2 -bottom-px h-px bg-gradient-to-r opacity-30 transition-opacity',
          !disabled && 'group-hover/rpg:opacity-60',
          t.edge,
        )}
      />

      {/* Decorative layers (halo, wisps, sparks) are skipped entirely
       * on disabled buttons so hovering a disabled CTA doesn't flash
       * the glow (Tailwind's `group-hover:` still fires on disabled
       * groups). Keeps disabled states visually muted and predictable. */}
      {!disabled && (
        <>
          {/* glow halo */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute -inset-1 rounded-[7px] blur-xl opacity-0 scale-90 transition-all duration-300 ease-out',
              'group-hover/rpg:opacity-70 group-hover/rpg:scale-100',
              t.halo,
            )}
          />

          {/* ethereal vertical wisps */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-[5px] mix-blend-screen opacity-0 group-hover/rpg:opacity-100 transition-opacity duration-300"
          >
            <span
              className="absolute top-full w-[1px] h-[140%] left-[28%] bg-gradient-to-t from-transparent via-amber-200/60 to-transparent"
              style={{ animation: 'lobby-ember 2.4s ease-in-out infinite' }}
            />
            <span
              className="absolute top-full w-[1px] h-[140%] left-[62%] bg-gradient-to-t from-transparent via-amber-200/40 to-transparent"
              style={{ animation: 'lobby-ember 2.1s 0.35s ease-in-out infinite' }}
            />
          </span>

          {/* spark layer */}
          <span
            key={sparkKey}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-visible opacity-0 group-hover/rpg:opacity-100"
          >
            {sparks.map((s) => (
              <span
                key={s.id}
                className={cn('absolute left-1/2 bottom-1 w-1 h-1 rounded-full blur-[0.5px]', t.spark)}
                style={{
                  animation: `lobby-ember 0.85s ${s.delay}s ease-out forwards`,
                  ['--ember-from-x' as string]: `${s.fromX}px`,
                  ['--ember-to-x' as string]: `${s.toX}px`,
                }}
              />
            ))}
          </span>
        </>
      )}

      {iconLeft && <span className="relative flex items-center">{iconLeft}</span>}
      {/* `Slottable` must be a *direct* child of `Slot`, so we branch on
       * the render path:
       *  - asChild=true  → hand `children` (the user's element, e.g.
       *    `<a>`) to Slot via Slottable; it becomes the rendered root.
       *  - asChild=false → wrap text in `<span className="relative">`
       *    so it stacks above the absolute decorative siblings (the
       *    original RpgButton behavior). */}
      {asChild ? (
        <Slottable>{children}</Slottable>
      ) : (
        <span className="relative">{children}</span>
      )}
      {iconRight && <span className="relative flex items-center">{iconRight}</span>}
    </Comp>
  );
});
